import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import { reduxForm, Form, Field } from 'redux-form/immutable'
import zxcvbn from 'zxcvbn'
import cx from 'classnames'
import _ from 'lodash'

import { FORM_ID, FIELD, PASSWORD_STRENGTH_LIMIT, LOCALE_PREFIX } from './constants'

import Input from '../../../../../../../../../Form/components/Input'
import Button from '../../../../../../../../../Form/components/Button'

import hocConnect from './hocConnect'

import './style.scss'

function validate(values, props) {
    const errors = {}

    const currentPassword = values.get(FIELD.CURRENT_PASSWORD)
    if (!currentPassword) {
        errors[FIELD.CURRENT_PASSWORD] = props.intl.formatMessage({
            id: `${LOCALE_PREFIX}.message.error.currentPassword`,
        })
    }

    const newPassword = values.get(FIELD.NEW_PASSWORD)
    const newPasswordCheck = values.get(FIELD.NEW_PASSWORD_CHECK)
    if ((newPassword && zxcvbn(newPassword).score) <= PASSWORD_STRENGTH_LIMIT) {
        errors[FIELD.NEW_PASSWORD_CHECK] = props.intl.formatMessage({
            id: `${LOCALE_PREFIX}.message.error.passwordStrength`,
        })
    }
    if (newPassword !== newPasswordCheck) {
        errors[FIELD.NEW_PASSWORD_CHECK] = props.intl.formatMessage({
            id: `${LOCALE_PREFIX}.message.error.sameNewPassword`,
        })
    }
    if (!newPassword || !newPasswordCheck) {
        errors[FIELD.NEW_PASSWORD_CHECK] = props.intl.formatMessage({
            id: `${LOCALE_PREFIX}.message.error.emptyPasswords`,
        })
    }

    return errors
}

@injectIntl
@hocConnect
@reduxForm({ form: FORM_ID, touchOnChange: true, validate })
export default class EditProfileForm extends Component {
    static propTypes = {
        anyTouched: PropTypes.bool.isRequired,
        contentId: PropTypes.string.isRequired,
        error: PropTypes.string,
        handleSubmit: PropTypes.func.isRequired,
        intl: PropTypes.object.isRequired,
        invalid: PropTypes.bool.isRequired,
        pristine: PropTypes.bool.isRequired,
        submitDataForm: PropTypes.func.isRequired,
        submitSucceeded: PropTypes.bool.isRequired,
        submitting: PropTypes.bool.isRequired,
        untouch: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props)

        this.state = { passwordStrength: null }
    }

    onNewPasswordChange = (_e, newValue) => {
        const score = zxcvbn(newValue).score

        this.setState({
            passwordStrength: !_.isEmpty(newValue) ? score : null,
        })
    }

    onSubmit = (values) => {
        const { contentId, submitDataForm, untouch } = this.props

        submitDataForm(contentId, values)
        untouch(FIELD.CURRENT_PASSWORD, FIELD.NEW_PASSWORD, FIELD.NEW_PASSWORD_CHECK)
    }

    render() {
        const { error, intl, handleSubmit, anyTouched, pristine, submitting, submitSucceeded, invalid } = this.props
        const { passwordStrength } = this.state

        return (
            <Form className={'change-password-form'} onSubmit={handleSubmit(this.onSubmit)}>
                <div className={'currentPassword'}>
                    <Field
                        name={FIELD.CURRENT_PASSWORD}
                        component={Input}
                        type={'password'}
                        label={intl.formatMessage({ id: `${LOCALE_PREFIX}.currentPassword.label` })}
                        placeholder={intl.formatMessage({ id: `${LOCALE_PREFIX}.currentPassword.placeholder` })}
                        disabled={submitting}
                    />
                </div>
                <div className={'newPassword'}>
                    {passwordStrength !== null && (
                        <div className={cx('strength', `color-${passwordStrength}`)}>
                            <FormattedMessage
                                id={`${LOCALE_PREFIX}.passwordStrength`}
                                values={{ strength: passwordStrength }}
                            />
                        </div>
                    )}
                    <Field
                        name={FIELD.NEW_PASSWORD}
                        component={Input}
                        type={'password'}
                        label={intl.formatMessage({ id: `${LOCALE_PREFIX}.newPassword.label` })}
                        placeholder={intl.formatMessage({ id: `${LOCALE_PREFIX}.newPassword.placeholder` })}
                        disabled={submitting}
                        onChange={this.onNewPasswordChange}
                    />
                </div>
                <div className={'newPasswordCheck'}>
                    <Field
                        name={FIELD.NEW_PASSWORD_CHECK}
                        component={Input}
                        type={'password'}
                        placeholder={intl.formatMessage({ id: `${LOCALE_PREFIX}.newPasswordCheck.placeholder` })}
                        disabled={submitting}
                    />
                </div>
                <div className={'submit'}>
                    <div className={'messages'}>
                        {error && (
                            <div className={'error'}>
                                <FormattedMessage id={`${LOCALE_PREFIX}.message.error.${error}`} />
                            </div>
                        )}
                        {!anyTouched && pristine && submitSucceeded && (
                            <div className={'success'}>
                                <FormattedMessage id={`${LOCALE_PREFIX}.message.success`} />
                            </div>
                        )}
                    </div>
                    <div className={'submit-button'}>
                        <Button
                            text={intl.formatMessage({ id: `${LOCALE_PREFIX}.submitButton` })}
                            disabled={(invalid && !error) || pristine || submitting}
                            type={'text'}
                        />
                    </div>
                </div>
            </Form>
        )
    }
}
